import React, { useState, useEffect, useRef } from "react";
import { Button, Input, Space, Table, message, Modal } from "antd";
import {
  SearchOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ExpandAltOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import SuspensionHistoryDialog from "./suspensionHistoryModal";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import ViewTesterDetails from "../tester/viewTesterDetails";
import EditTester from "../tester/editTester";
import AddTesterForm from "../tester/addTester";
import "./testersV2.css";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const apiUrl = process.env.REACT_APP_API_Testers;
const specialTesterDeleteApiUrl = "https://ioum0e9hnf.execute-api.us-east-1.amazonaws.com/dev/delete-special-testers";
const suspensionHistoryApiUrl = process.env.REACT_APP_SuspensionHistory;

const formatLastLogin = (timeString) => {
  if (!timeString) return "";
  if (timeString === "Haven't logged in yet") return "Haven't logged in yet";

  const matches = timeString.match(/\d+/g);
  if (!matches) return timeString;

  let days = 0;
  let hours = 0;
  let minutes = 0;

  if (timeString.includes("day")) {
    days = matches[0] ? parseInt(matches[0], 10) : 0;
    if (timeString.includes("hour")) hours = matches[1] ? parseInt(matches[1], 10) : 0;
  } else if (timeString.includes("hour")) {
    hours = matches[0] ? parseInt(matches[0], 10) : 0;
    if (timeString.includes("minute")) minutes = matches[1] ? parseInt(matches[1], 10) : 0;
  } else if (timeString.includes("minute")) {
    minutes = matches[0] ? parseInt(matches[0], 10) : 0;
  }

  const totalHours = hours + minutes / 60;
  days += Math.floor(totalHours / 24);
  hours = Math.floor(totalHours % 24);
  minutes = Math.floor(minutes % 60);

  const daysInYear = 365.25;
  const daysInMonth = 30.44;
  const years = Math.floor(days / daysInYear);
  const remainingDaysAfterYears = days % daysInYear;
  const months = Math.floor(remainingDaysAfterYears / daysInMonth);
  const remainingDays = Math.floor(remainingDaysAfterYears % daysInMonth);

  let result = "";
  if (years > 0) result += `${years} year${years > 1 ? "s" : ""} `;
  if (months > 0) result += `${months} month${months > 1 ? "s" : ""} `;
  if (remainingDays > 0) result += `${remainingDays} day${remainingDays > 1 ? "s" : ""} `;
  if (hours > 0) result += `${hours} hour${hours !== 1 ? "s" : ""} `;
  if (minutes > 0) result += `${minutes} minute${minutes !== 1 ? "s" : ""}`;

  return result.trim() || "0 minutes";
};

const TestersV2 = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [testers, setTesters] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [open, setOpen] = useState(false);
  const [testerId, setTesterId] = useState(null);
  const [searchedColumn, setSearchedColumn] = useState("");
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [viewModalVisible, setviewModalVisible] = useState(false);
  const [editModalVisible, setEidtModalVisible] = useState(false);
  const [suspensionHistory, setSuspensionHistory] = useState([]);
  const [suspensionCount, setSuspensionCount] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const searchInput = useRef(null);

  const storedUser = localStorage.getItem("user");
  const user = JSON.parse(storedUser || "{}");

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  useEffect(() => {
    fetchData();
  }, [open, editModalVisible]);

  const fetchData = async () => {
    try {
      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error("Failed to fetch testers");
      const data = await response.json();
      const formattedData = (data.body || []).map((tester) => ({
        ...tester,
        last_login: formatLastLogin(tester.last_login),
      }));
      setTesters(formattedData);
    } catch (error) {
      console.error("Error fetching testers:", error);
    } finally {
      setLoading(false);
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div className="testers-v2-filter" onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
        />
        <Space className="testers-v2-filter-actions">
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            Search
          </Button>
          <Button size="small" onClick={() => clearFilters && handleReset(clearFilters)}>
            Reset
          </Button>
          <Button type="link" size="small" onClick={() => close()}>
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined className={filtered ? "testers-v2-filter-active" : ""} />
    ),
    onFilter: (value, record) => {
      const val = record[dataIndex];
      if (val == null) return false;
      return val.toString().toLowerCase().includes(value.toLowerCase());
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) setTimeout(() => searchInput.current?.select(), 100);
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "rgba(56, 189, 248, 0.3)", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text != null ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const handleViewClick = (record) => {
    setviewModalVisible(true);
    setTesterId(record.id);
  };

  const handleEditClick = (record) => {
    setEidtModalVisible(true);
    setTesterId(record.id);
  };

  const handleDeleteClick = (record) => {
    setSelectedRecord(record);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const deleteUrl =
        user.roleId === 7
          ? `${specialTesterDeleteApiUrl}/${selectedRecord.id}`
          : `${apiUrl}/${selectedRecord.id}`;

      const response = await fetch(deleteUrl, { method: "DELETE" });

      if (response.ok) {
        messageApi.success("Tester deleted successfully");
        setTesters(testers.filter((r) => r.id !== selectedRecord.id));
      } else {
        messageApi.error("Failed to delete tester");
      }
    } catch (error) {
      messageApi.error("Failed to delete tester");
    }
    setDeleteModalVisible(false);
  };

  const handleAddClick = () => setOpen(true);

  const handleSuspensionCountClick = async (record) => {
    try {
      const response = await fetch(`${suspensionHistoryApiUrl}/${record.id}`);
      const data = await response.json();
      const parsedData = JSON.parse(data.body);
      setSuspensionHistory(parsedData.history);
      setSuspensionCount(parsedData.history.length);
      setIsDialogOpen(true);
    } catch (error) {
      console.error("Failed to fetch suspension history", error);
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "first_name",
      key: "first_name",
      width: "14%",
      ...getColumnSearchProps("first_name"),
      sorter: (a, b) => (a.first_name || "").localeCompare(b.first_name || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: "16%",
      ...getColumnSearchProps("email"),
      sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Role",
      dataIndex: "role_id",
      key: "role_id",
      width: "12%",
      sorter: (a, b) => (a.role_id || 0) - (b.role_id || 0),
      sortDirections: ["descend", "ascend"],
      render: (role_id) => {
        switch (role_id) {
          case 4: return "Manual Tester";
          case 6: return "Internal Team";
          case 7: return "Special Tester";
          case 9: return "Burkina Tester";
          default: return "Unknown Role";
        }
      },
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
      width: "10%",
      ...getColumnSearchProps("gender"),
      sorter: (a, b) => (a.gender || "").localeCompare(b.gender || ""),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Suspension",
      dataIndex: "suspension_count",
      key: "suspension_count",
      width: "10%",
      sorter: (a, b) => (a.suspension_count || 0) - (b.suspension_count || 0),
      sortDirections: ["descend", "ascend"],
      render: (text, record) =>
        text > 0 ? (
          <Button
            type="link"
            onClick={() => handleSuspensionCountClick(record)}
            className="testers-v2-suspension-link"
          >
            {text}
          </Button>
        ) : (
          <span>{text}</span>
        ),
    },
    {
      title: "Last Login",
      dataIndex: "last_login",
      key: "last_login",
      width: "14%",
      ...getColumnSearchProps("last_login"),
      sorter: (a, b) => (a.last_login || "").toString().localeCompare((b.last_login || "").toString()),
      sortDirections: ["descend", "ascend"],
    },
    {
      title: "Actions",
      key: "actions",
      width: "24%",
      render: (_, record) => (
        <div className="testers-v2-actions">
          <Button
            type="default"
            icon={<ExpandAltOutlined />}
            onClick={() => handleViewClick(record)}
            className="testers-v2-btn-view"
          >
            View
          </Button>
          <Button
            type="default"
            icon={<EditOutlined />}
            onClick={() => handleEditClick(record)}
            className="testers-v2-btn-edit"
          >
            Edit
          </Button>
          <Button
            type="default"
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteClick(record)}
            danger
            className="testers-v2-btn-delete"
          >
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="testers-v2">
      {contextHolder}
      <div className="testers-v2-header">
        <h1 className="testers-v2-title">All Testers</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddClick}
          className="testers-v2-add-btn"
        >
          Add Tester
        </Button>
      </div>

      <div className="testers-v2-card">
        <Table
          columns={columns}
          dataSource={testers}
          loading={loading}
          rowKey="id"
          locale={{ emptyText: loading ? "Loading..." : "No testers yet" }}
          pagination={{
            showSizeChanger: true,
            defaultPageSize: 10,
            pageSizeOptions: ["5", "10", "20", "50"],
            showTotal: (total) => `Total ${total} testers`,
          }}
          className="testers-v2-table"
          scroll={{ x: "max-content" }}
        />
      </div>

      <Modal
        title="Confirm Delete"
        open={deleteModalVisible}
        onOk={handleDeleteConfirm}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
        className="testers-v2-delete-modal"
        width={420}
      >
        <p className="testers-v2-delete-text">
          Are you sure you want to delete{" "}
          <strong>
            {selectedRecord?.first_name} {selectedRecord?.last_name}
          </strong>
          ? This action cannot be undone.
        </p>
      </Modal>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        className="testers-v2-dialog"
        PaperProps={{
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <AddTesterForm setOpen={setOpen} />
      </Dialog>

      <Dialog
        open={viewModalVisible}
        onClose={() => setviewModalVisible(false)}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        className="testers-v2-dialog"
        PaperProps={{
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <ViewTesterDetails setOpen={setviewModalVisible} testerId={testerId} />
      </Dialog>

      <Dialog
        open={editModalVisible}
        onClose={() => setEidtModalVisible(false)}
        TransitionComponent={Transition}
        maxWidth="sm"
        fullWidth
        className="testers-v2-dialog"
        PaperProps={{
          sx: {
            borderRadius: 8,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
            maxHeight: "90vh",
          },
        }}
      >
        <EditTester setOpen={setEidtModalVisible} id={testerId} />
      </Dialog>

      <SuspensionHistoryDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        suspensionHistory={suspensionHistory}
        suspensionCount={suspensionCount}
      />
    </div>
  );
};

export default TestersV2;
